<template>
  <div class="carousel-manager">
    <div class="panel-toolbar">
      <p class="panel-toolbar-meta">Gestioná las imágenes del carrusel de la home</p>
      <div class="panel-toolbar-actions">
        <button class="btn btn-primary" @click="openAddModal">
          <i class="fas fa-plus me-2"></i>Agregar imagen
        </button>
      </div>
    </div>

    <!-- Hero Preview & Controls in New Layout -->
    <div class="row g-4">
      <!-- Left Column: Preview -->
      <div class="col-lg-7 ps-0">
        <div class="hero-preview-container">
          <h4 class="preview-title text-white">
            Vista Previa 
          </h4>
          
          <div class="hero-preview-wrapper">
            <div 
              class="hero-preview-section" 
              :class="{
                'with-image': activePreviewImage
              }"
              :style="activePreviewImage ? `background-image: url(${activePreviewImage})` : ''"
            >
              <div class="preview-message" v-if="!activePreviewImage">
                <i class="fas fa-image"></i>
                <p class="text-white fw-normal">Selecciona una imagen para previsualizar</p>
                  </div>
              <div class="preview-overlay" v-if="activePreviewImage">
                <div class="preview-info">
                  <h3>{{ currentPreviewImageInfo?.title || 'Sin título' }}</h3>
                  <p v-if="currentPreviewImageInfo?.link" class="text-white fw-normal">Enlace: {{ currentPreviewImageInfo.link }}</p>
                  <span class="preview-status active">
                    Configurado
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="preview-actions mt-3" v-if="currentPreviewImageInfo">
            <button 
              class="btn btn-sm btn-primary me-2" 
              @click="openEditModal(currentPreviewImageInfo)"
            >
              <i class="fas fa-edit me-2"></i>Editar imagen
            </button>
            <button 
              class="btn btn-sm btn-outline-danger" 
              @click="confirmDeleteImage(currentPreviewImageInfo)"
            >
              <i class="fas fa-trash me-2"></i>Eliminar imagen
            </button>
          </div>
        </div>
      </div>
      
      <!-- Right Column: Order Controls -->
      <div class="col-lg-5">
        <div class="order-control-container">
          <h4 class="order-title pb-2 text-white">Orden de Imágenes</h4>
          
          <div class="order-list">
            <transition-group name="order-list">
              <div 
                v-for="(image, index) in sortedImages" 
                :key="image.id"
                class="order-item"
                :class="{'active': currentEditingIndex === index}"
                @click="handleSlotClick(index)"
              >
                <div class="order-number">{{ index + 1 }}</div>
                <div class="order-image">
                  <img :src="image.image" :alt="image.title">
                </div>
                <div class="order-details">
                  <span class="order-title">{{ image.title }}</span>
                  <span class="order-status" :class="{'active': image.is_active}">
                    Activo
                  </span>
                </div>
                <div class="order-actions">
                  <button 
                    class="btn btn-sm btn-outline-light"
                    @click.stop="moveImageOrder(image, 'up')"
                    :disabled="index === 0"
                    title="Mover arriba"
                  >
                    <i class="fas fa-chevron-up"></i>
                  </button>
                  <button 
                    class="btn btn-sm btn-outline-light"
                    @click.stop="moveImageOrder(image, 'down')"
                    :disabled="index === sortedImages.length - 1"
                    title="Mover abajo"
                  >
                    <i class="fas fa-chevron-down"></i>
                  </button>
                </div>
              </div>
            </transition-group>
            
            <div class="empty-order-message" v-if="sortedImages.length === 0">
              <i class="fas fa-info-circle"></i>
              <p>No hay imágenes agregadas</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Slots Grid with improved UI -->
    <div class="image-slots-section mt-4">
      <div class="slots-header d-flex justify-content-between align-items-center">
        <h3 class="slots-title text-white">Imágenes del Carousel <span class="badge bg-secondary ms-2">{{ carouselImages.length }}/4</span></h3>
      </div>
      
      <div class="image-slots-grid">
        <div 
          v-for="index in 4" 
          :key="index"
          class="image-slot-card"
          :class="{ 
            'has-image': getSlotImage(index - 1),
            'active': currentEditingIndex === index - 1
          }"
          @click="handleSlotClick(index - 1)"
        >
          <div class="slot-number">{{ index }}</div>
          <div class="image-slot">
            <template v-if="getSlotImage(index - 1) && shouldShowImage(getSlotImage(index - 1))">
              <img :src="getSlotImage(index - 1).image" :alt="getSlotImage(index - 1).title">
              <div class="slot-overlay">
                <div class="slot-info">
                  <h5 class="slot-title">{{ getSlotImage(index - 1).title }}</h5>
                  <span class="slot-status active">
                    Configurado
                  </span>
                </div>
                <div class="slot-actions">
                  <button class="btn btn-sm btn-light" @click.stop="openEditModal(getSlotImage(index - 1))">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="btn btn-sm btn-danger" @click.stop="confirmDeleteImage(getSlotImage(index - 1))">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </template>
            <div class="empty-slot" v-else>
              <i class="fas fa-plus"></i>
              <p v-if="carouselImages.length < 4">Click para agregar</p>
              <p v-else>Máximo alcanzado</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for adding/editing images -->
    <div class="modal fade custom-modal" :class="{ 'show d-block': showModal }" v-if="showModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditing ? 'Editar' : 'Agregar' }} Imagen</h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <form @submit.prevent="saveImage">
            <div class="modal-body bg-dark">
              <div class="row mb-4">
                <div class="col-md-6 p-2">
                  <div class="form-group mb-4">
                    <label for="imageLink" class="form-label">Enlace de destino</label>
                    <input 
                      type="url" 
                      id="imageLink" 
                      class="form-control" 
                      v-model="currentImage.link"
                      placeholder="https://ejemplo.com o /productos"
                    >
                    <small class="text-muted">URL a la que dirigirá cuando se haga clic en la imagen</small>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="desktopImage" class="form-label">Imagen principal *</label>
                    <input 
                      type="file" 
                      id="desktopImage" 
                      class="form-control" 
                      accept="image/*"
                      @change="handleDesktopImageChange"
                      :required="!isEditing"
                    >
                    <small class="text-muted">
                      <i class="fas fa-info-circle me-1"></i>
                      Resolución recomendada: 1920×620 px (relación 3.1:1)
                    </small>
                  </div>
                  
                  <div class="form-group mb-3">
                    <label for="mobileImage" class="form-label">Imagen móvil (opcional)</label>
                    <input 
                      type="file" 
                      id="mobileImage" 
                      class="form-control" 
                      accept="image/*"
                      @change="handleMobileImageChange"
                    >
                    <small class="text-muted">
                      <i class="fas fa-mobile-alt me-1"></i>
                      Imagen optimizada para dispositivos móviles
                    </small>
                  </div>
                  
                  <div class="image-preview-section">
                    <label class="form-label">Vista previa</label>
                    <div class="preview-container">
                      <div 
                        class="preview-img" 
                        :style="imagePreview ? `background-image: url(${imagePreview})` : ''"
                      >
                        <div v-if="!imagePreview" class="no-preview">
                          <i class="fas fa-image"></i>
                          <p>Selecciona una imagen</p>
                        </div>
                        <div class="preview-badge" v-if="imagePreview">Principal</div>
                      </div>
                      
                      <div 
                        v-if="mobileImagePreview" 
                        class="preview-img mobile-preview mt-2" 
                        :style="`background-image: url(${mobileImagePreview})`"
                      >
                        <div class="preview-badge">Móvil</div>
                      </div>
                    </div>
                    
                    <div 
                      class="size-warning" 
                      v-if="imagePreview && !isRecommendedSize"
                    >
                      <i class="fas fa-exclamation-triangle me-2"></i>
                      Tamaño no recomendado. Ideal: 1920×620px
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeModal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                {{ isEditing ? 'Actualizar' : 'Guardar' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- Delete confirmation modal -->
    <div class="modal fade custom-modal" :class="{ 'show d-block': showDeleteModal }" v-if="showDeleteModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar eliminación</h5>
            <button type="button" class="btn-close" @click="closeDeleteModal"></button>
          </div>
          <div class="modal-body">
            <p class="text-danger mt-3">Esta acción no se puede deshacer.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDeleteModal">Cancelar</button>
            <button 
              type="button" 
              class="btn btn-danger" 
              @click="deleteImage"
              :disabled="deleting"
            >
              <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Backdrop for modals -->
    <div class="modal-backdrop fade show" v-if="showModal || showDeleteModal"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useToast } from 'vue-toastification';
import { useUnsavedChangesGuard, useFormDirty } from '@/composables/useUnsavedChangesGuard';
import { 
  getCarouselImages, 
  uploadCarouselImage, 
  updateCarouselImage, 
  deleteCarouselImage,
  updateCarouselImageOrder
} from '@/services/banners';

const toast = useToast();

// State
const carouselImages = ref([]);  // Initialize as empty array
const loading = ref(true);
const showModal = ref(false);
const showDeleteModal = ref(false);
const saving = ref(false);
const deleting = ref(false);
const currentImage = ref({
  title: '',
  link: 'https://kameseeds.com/productos',
  is_active: true,
  order: 0
});
const imageToDelete = ref(null);

// Improved UX state
const imagePreview = ref(null);

// File refs
const desktopImageFile = ref(null);
const mobileImageFile = ref(null);

// Preview state
const activePreviewImage = ref(null);
const currentEditingIndex = ref(null);
const mobileImagePreview = ref(null);

// Additional state for image dimensions
const imageDimensions = ref({ width: 0, height: 0 });

// Edición de imagen
const isEditing = ref(false);

// Cambios sin guardar: campos del modal de banner o archivos de imagen elegidos.
const bannerFormDirty = useFormDirty(currentImage);
const bannerDirty = computed(() =>
  showModal.value &&
  (bannerFormDirty.dirty.value || !!(desktopImageFile.value || mobileImageFile.value))
);

useUnsavedChangesGuard({
  dirty: bannerDirty,
  saveFn: () => saveImage(),
  message: 'El formulario del banner tiene cambios sin guardar. Si salís ahora, se van a perder.',
});

// Computed property for sorted images by order
const sortedImages = computed(() => {
  // Make sure carouselImages.value is an array before trying to sort it
  if (!carouselImages.value || !Array.isArray(carouselImages.value)) {
    return [];
  }
  return [...carouselImages.value].sort((a, b) => a.order - b.order);
});

// Computed property to check if the image is of recommended size
const isRecommendedSize = computed(() => {
  if (!imageDimensions.value.width) return true;
  
  return (imageDimensions.value.width === 1920 && imageDimensions.value.height === 800);
});

// Computed property for current preview image info
const currentPreviewImageInfo = computed(() => {
  if (currentEditingIndex.value === null) return null;
  return getSlotImage(currentEditingIndex.value);
});

// Helper method to check if an image should be shown based on filters
const shouldShowImage = (image) => {
  if (!image) return false;
  return Boolean(image.image);
};

// Method to handle reordering images
const moveImageOrder = async (image, direction) => {
  if (!sortedImages.value.length) return;
  
  const currentIndex = sortedImages.value.findIndex(img => img.id === image.id);
  if (currentIndex === -1) return;
  
  if (direction === 'up' && currentIndex > 0) {
    const targetImage = sortedImages.value[currentIndex - 1];
    await swapImageOrder(image, targetImage);
  } else if (direction === 'down' && currentIndex < sortedImages.value.length - 1) {
    const targetImage = sortedImages.value[currentIndex + 1];
    await swapImageOrder(image, targetImage);
  }
};

// Method to swap image order
const swapImageOrder = async (image1, image2) => {
  if (!image1 || !image2) return;
  
  const tempOrder = image1.order;
  
  loading.value = true;
  try {
    await updateCarouselImageOrder(image1.id, image2.order, image2.id, tempOrder);
    await fetchImages();
    toast.success('Orden actualizado correctamente');
  } catch (err) {
    toast.error('Error al actualizar el orden');
    console.error(err);
  } finally {
    loading.value = false;
  }
};

// Methods
const fetchImages = async () => {
  loading.value = true;
  
  // Limpiar caché para forzar carga fresca
  localStorage.removeItem('carousel_images');
  localStorage.removeItem('carousel_images_timestamp');
  
  try {
    console.log('Solicitando imágenes del carousel...');
    const data = await getCarouselImages(true);
    
    const bannerArray = Array.isArray(data) ? data : (data?.results || []);
    
    if (Array.isArray(bannerArray)) {
      const normalizedData = bannerArray
        .map(image => ({
          id: image.id,
          image: image.image_url || image.image || '',
          title: image.title || '',
          link: image.url_to || image.link || '',
          is_active: image.is_active !== undefined ? image.is_active : true,
          order: image.order || 0,
          mobileImage: image.mobile_image_url || image.mobileImage || ''
        }))
        .sort((a, b) => a.order - b.order);
      
      carouselImages.value = normalizedData;
    } else {
      console.warn('Los datos recibidos no son un array:', data);
      carouselImages.value = [];
    }
    
    // Reset currentEditingIndex if the selected image no longer exists
    if (currentEditingIndex.value !== null && 
        !carouselImages.value[currentEditingIndex.value]) {
      currentEditingIndex.value = null;
      activePreviewImage.value = null;
    }
  } catch (err) {
    console.error('Error al cargar imágenes del carousel:', err);
    toast.error('No se pudieron cargar las imágenes del carousel: ' + (err.message || 'Error desconocido'));
  } finally {
    loading.value = false;
  }
};

// Safely get a slot image by sorted order index
const getSlotImage = (index) => {
  if (!sortedImages.value || !Array.isArray(sortedImages.value)) {
    return null;
  }
  return sortedImages.value[index] || null;
};

const handleSlotClick = (index) => {
  const image = getSlotImage(index);
  if (image) {
    currentEditingIndex.value = index;
    activePreviewImage.value = image.image;
  } else if (index >= 0 && carouselImages.value.length < 4) {
    // If no image in this slot and we have less than 4 images, open add modal
    currentEditingIndex.value = index;
    openAddModal();
  }
};

// Open modal for adding a new image
const openAddModal = () => {
  currentImage.value = {
    title: '',
    link: 'https://kameseeds.com/productos',
    is_active: true,
    order: carouselImages.value.length
  };
  
  isEditing.value = false;
  desktopImageFile.value = null;
  imagePreview.value = null;
  mobileImageFile.value = null;
  mobileImagePreview.value = null;
  bannerFormDirty.markClean();
  showModal.value = true;
};

// Open modal for editing existing image
const openEditModal = (image) => {
  currentImage.value = { 
    id: image.id,
    title: image.title || '',
    link: image.link || 'https://kameseeds.com/productos',
    is_active: true,
    order: image.order
  };
  
  isEditing.value = true;
  imagePreview.value = image.image;
  mobileImagePreview.value = image.mobileImage || null;
  bannerFormDirty.markClean();
  showModal.value = true;
};

// Save new or update existing image
const saveImage = async () => {
  
  if (!isEditing.value && !desktopImageFile.value) {
    toast.error('La imagen principal es requerida');
    return;
  }
  
  saving.value = true;
  
  try {
    // Asegurarse de que las propiedades están inicializadas
    if (!currentImage.value.title) {
      currentImage.value.title = '';
    }
    
    const imageData = {
      title: currentImage.value.title.trim(),
      link: currentImage.value.link || 'https://kameseeds.com/productos',
      isActive: true, // Siempre activo ahora
      order: currentImage.value.order || 0
    };
    
    if (desktopImageFile.value) {
      imageData.file = desktopImageFile.value;
    }
    if (mobileImageFile.value) {
      imageData.mobileFile = mobileImageFile.value;
    }
    
    if (isEditing.value) {
      // Actualizar imagen existente
      await updateCarouselImage(currentImage.value.id, imageData);
      toast.success('Imagen actualizada correctamente');
    } else {
      // Crear nueva imagen
      await uploadCarouselImage(imageData);
      toast.success('Imagen agregada correctamente');
    }
    
    await fetchImages();
    closeModal();
  } catch (error) {
    console.error('Error saving image:', error);
    const data = error.response?.data;
    const detail = data?.detail || data?.image_url?.[0] || data?.non_field_errors?.[0];
    toast.error(detail || error.message || 'Error al guardar la imagen');
  } finally {
    saving.value = false;
  }
};

// Confirm image deletion
const confirmDeleteImage = (image) => {
  imageToDelete.value = image;
  showDeleteModal.value = true;
};

// Delete image
const deleteImage = async () => {
  if (!imageToDelete.value) return;
  
  deleting.value = true;
  try {
    await deleteCarouselImage(imageToDelete.value.id);
    await fetchImages();
    toast.success('Imagen eliminada correctamente');
    
    // Reset preview if we deleted the image we were previewing
    if (currentEditingIndex.value !== null && 
        carouselImages.value.length <= currentEditingIndex.value) {
      currentEditingIndex.value = null;
      activePreviewImage.value = null;
    }
    
    closeDeleteModal();
  } catch (error) {
    console.error('Error deleting image:', error);
    toast.error('Error al eliminar la imagen');
  } finally {
    deleting.value = false;
  }
};

// Close modals
const closeModal = () => {
  showModal.value = false;
  currentImage.value = {
    title: '',
    link: 'https://kameseeds.com/productos',
    is_active: true,
    order: 0
  };
  desktopImageFile.value = null;
  imagePreview.value = null;
  mobileImageFile.value = null;
  mobileImagePreview.value = null;
  bannerFormDirty.markClean();
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  imageToDelete.value = null;
};

// Methods for file handling and image dimension checking
const checkImageDimensions = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        imageDimensions.value = {
          width: img.width,
          height: img.height
        };
        resolve(e.target.result);
      };
    };
  });
};

const handleDesktopImageChange = async (event) => {
  if (!event.target.files.length) return;
  
  const file = event.target.files[0];
  desktopImageFile.value = file;
  imagePreview.value = await checkImageDimensions(file);
};

const handleMobileImageChange = async (event) => {
  if (!event.target.files.length) return;
  const file = event.target.files[0];
  mobileImageFile.value = file;
  // Previsualización simple
  const reader = new FileReader();
  reader.onload = (e) => {
    mobileImagePreview.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

// Initialize
onMounted(() => {
  fetchImages();
});
</script>

<style scoped>
.carousel-manager {
  color: var(--white-color);
}

/* Header styles */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.section-title {
  margin-bottom: 0;
  font-size: 1.75rem;
  color: var(--white-color);
}

.section-subtitle {
  color: var(--white-color-2);
  margin-top: 5px;
}

/* Hero Preview Section - Updated to be smaller */
.hero-preview-container, .order-control-container {
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 10px;
  color: var(--white-color);
  padding: 20px;
  height: 100%;
  box-shadow: 0 2px 5px var(--shadow-light);
}

.preview-title, .order-title {
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--white-color);
}

.hero-preview-wrapper {
  position: relative;
  margin-bottom: 15px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3px 6px var(--shadow-light);
}

.hero-preview-section {
  background-color: rgba(0, 0, 0, 0.25);
  height: 200px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
  position: relative;
  color: var(--white-color);
}

.hero-preview-section.with-image {
  color: var(--white-color);
}

.preview-size-note {
  display: block;
  font-size: 0.8rem;
  color: var(--white-color-2);
  font-weight: normal;
  margin-top: 5px;
}

.size-recommendation {
  font-size: 0.8rem;
  color: var(--white-color-2);
  margin-left: 10px;
  font-weight: normal;
}

.preview-dimensions {
  font-size: 0.8rem;
  color: var(--white-color-2);
  margin-left: auto;
}

/* Order Control Styles */
.order-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--panel-border);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.15);
}

.order-item {
  display: flex;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid var(--panel-border-muted);
  cursor: pointer;
  transition: all 0.2s;
  background: transparent;
  color: var(--white-color);
}

.order-item:last-child {
  border-bottom: none;
}

.order-item:hover {
  background-color: var(--black-color-2);
}

.order-item.active {
  background-color: var(--black-color-2);
  border-left: 3px solid var(--primary-color);
}

.order-number {
  width: 30px;
  height: 30px;
  background-color: var(--primary-color);
  color: var(--white-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  font-weight: bold;
}

.order-image {
  width: 50px;
  height: 35px;
  margin-right: 10px;
  border-radius: 4px;
  overflow: hidden;
}

.order-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.order-details {
  flex: 1;
  color: var(--white-color);
}

.order-title {
  display: block;
  font-weight: 500;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  color: var(--white-color);
}

.order-status {
  display: inline-block;
  padding: 1px 5px;
  font-size: 0.7rem;
  background-color: var(--black-color-2);
  border-radius: 3px;
  color: var(--white-color-2);
}

.order-status.active {
  background-color: var(--primary-color);
  color: var(--black-color-3);
}

.order-actions {
  display: flex;
}

.empty-order-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  color: var(--white-color-2);
}

.empty-order-message i {
  font-size: 2rem;
  margin-bottom: 10px;
  color: var(--black-color-2);
}

/* Hero preview section */
.hero-preview-section.with-image {
  position: relative;
}

.preview-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--white-color-2);
}

.preview-message i {
  font-size: 2rem;
  margin-bottom: 10px;
  color: var(--white-color-2);
}

.preview-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0));
  padding: 20px;
  color: var(--white-color);
  text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
}

.preview-info h3 {
  margin-bottom: 5px;
  font-size: 1.5rem;
  color: var(--white-color);
}

.preview-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: var(--black-color-2);
  color: var(--white-color-2);
  margin-top: 5px;
  font-size: 0.8rem;
}

.preview-status.active {
  background-color: var(--primary-color);
  color: var(--black-color-3);
}

.preview-controls {
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
}

.preview-control {
  width: 30px;
  height: 30px;
  background-color: rgba(255,255,255,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-left: 10px;
  cursor: pointer;
}

.preview-indicators {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
}

.preview-dot {
  width: 8px;
  height: 8px;
  background-color: rgba(255,255,255,0.5);
  border-radius: 50%;
  margin: 0 5px;
}

.preview-dot.active {
  background-color: white;
}

/* Image Slots Section */
.image-slots-section {
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 10px;
  padding: 20px;
  box-shadow: none;
}

.slots-header {
  margin-bottom: 20px;
}

.slots-title {
  margin-bottom: 0;
  font-size: 1.3rem;
}

.image-slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.image-slot-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3px 6px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid transparent;
}

.image-slot-card.active {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--shadow-hover);
}

.image-slot-card.inactive .image-slot::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.4);
  z-index: 1;
}

.slot-number {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 25px;
  height: 25px;
  background-color: var(--primary-color);
  color: var(--white-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
  z-index: 2;
}

.image-slot {
  height: 180px;
  position: relative;
  background-color: var(--black-color-2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-slot img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.empty-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--white-color-2);
}

.image-slot:hover .empty-slot {
  color: var(--primary-color);
}

.slot-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 15px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 2;
}

.image-slot:hover .slot-overlay {
  opacity: 1;
}

.slot-info {
  color: white;
}

.slot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.slot-title {
  margin: 0;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.slot-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: var(--navbar-border);
  color: var(--text-muted);
  font-size: 0.8rem;
}

.slot-status.active {
  background-color: var(--pastel-mint);
  color: var(--primary-color);
}

.slot-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Modal Styles */
.custom-modal .modal-content {
  border-radius: 10px;
  border: 1px solid var(--panel-border);
  background-color: var(--grey-color);
  color: var(--white-color);
}

.custom-modal .modal-header {
  background-color: rgba(0, 0, 0, 0.12);
  border-bottom: 1px solid var(--panel-border);
  color: var(--white-color);
}

.custom-modal .modal-title {
  font-weight: 600;
  color: var(--white-color);
}

.custom-modal .modal-footer {
  background-color: rgba(0, 0, 0, 0.12);
  border-top: 1px solid var(--panel-border);
}

.custom-modal .form-control,
.custom-modal textarea,
.custom-modal input[type="text"],
.custom-modal input[type="file"] {
  background-color: var(--black-color-2);
  color: var(--white-color);
  border: 1px solid var(--black-color-2);
}

.custom-modal .form-control::placeholder,
.custom-modal textarea::placeholder {
  color: var(--white-color-2);
  opacity: 1;
}

.custom-modal label {
  color: var(--white-color-2);
}

.custom-modal .form-check-label {
  color: var(--white-color-2);
}

.custom-modal .text-muted {
  color: var(--white-color-2) !important;
}

/* Ajusto el área de previsualización de imagen */
.image-preview-area {
  border: 1px solid var(--black-color-2);
  background-color: var(--black-color-2);
  color: var(--white-color);
}

.delete-preview {
  border: 1px solid var(--black-color-2);
  background-color: var(--black-color-2);
  color: var(--white-color);
}

.preview-img {
  height: 180px;
  width: 100%;
  background-size: cover;
  background-position: center;
  border-radius: 4px;
  margin-bottom: 10px;
  position: relative;
  overflow: hidden;
}

.no-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #adb5bd;
}

.no-preview i {
  font-size: 2rem;
  margin-bottom: 10px;
}

/* Add these styles to ensure modals display correctly */
.modal.show.d-block {
  display: block;
  z-index: 1050;
}

/* Make sure the modal-backdrop doesn't block modal visibility */
.modal-backdrop {
  z-index: 1040;
}

.slot-with-image {
  width: 100%;
  height: 100%;
}

.preview-actions {
  display: flex;
  justify-content: center;
}

/* Order-list transition */
.order-list-enter-active, .order-list-leave-active {
  transition: all 0.5s ease;
}
.order-list-enter-from, .order-list-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.size-warning {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  background-color: rgba(255, 193, 7, 0.1);
  border: 1px solid #ffc107;
  color: #ffc107;
  padding: 8px 12px;
  border-radius: 6px;
  margin-top: 10px;
}

/* Enhanced form styles */
.form-label {
  font-weight: 600;
  color: var(--white-color);
  margin-bottom: 8px;
}

.form-control {
  background-color: var(--black-color-2);
  border: 1px solid var(--black-color-2);
  color: var(--white-color);
  border-radius: 8px;
  padding: 12px 16px;
  transition: all 0.3s ease;
}

.form-control:focus {
  background-color: var(--black-color-2);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: 0 0 0 0.2rem rgba(var(--primary-color-rgb), 0.25);
}

.form-control::placeholder {
  color: var(--white-color-2);
}

/* Enhanced preview section */
.image-preview-section {
  background: var(--black-color-2);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid var(--black-color-1);
}

.preview-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.preview-img {
  height: 120px;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  border: 2px dashed var(--black-color-1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease;
}

.preview-img:hover {
  border-color: var(--primary-color);
}

.mobile-preview {
  height: 80px;
}

.no-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--white-color-2);
}

.no-preview i {
  font-size: 2rem;
  margin-bottom: 8px;
  color: var(--black-color-1);
}

.no-preview p {
  margin: 0;
  font-size: 0.9rem;
}

.preview-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--primary-color);
  color: var(--black-color-3);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
}

/* Modal improvements */
.modal-content {
  background-color: var(--grey-color);
  border: 1px solid var(--panel-border);
  border-radius: 12px;
  color: var(--white-color);
}

.modal-header {
  border-bottom: 1px solid var(--panel-border);
  padding: 20px 24px;
}

.modal-title {
  color: var(--white-color);
  font-weight: 600;
}

.btn-close {
  filter: invert(1);
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  border-top: 1px solid var(--black-color-2);
  padding: 16px 24px;
}

/* Button improvements */
.btn {
  border-radius: 8px;
  font-weight: 500;
  padding: 10px 20px;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.btn-primary:hover {
  background-color: var(--primary-color-dark);
  border-color: var(--primary-color-dark);
  transform: translateY(-1px);
}

.btn-secondary {
  background-color: var(--black-color-2);
  border-color: var(--black-color-2);
  color: var(--white-color);
}

.btn-secondary:hover {
  background-color: var(--black-color-1);
  border-color: var(--black-color-1);
  color: var(--white-color);
}

/* Enhanced grid layout */
.image-slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.image-slot-card {
  background: var(--black-color-2);
  border-radius: 12px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
}

.image-slot-card:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}

.image-slot-card.active {
  border-color: var(--primary-color);
  background: var(--black-color-1);
}

.slot-number {
  position: absolute;
  top: 10px;
  left: 10px;
  background: var(--primary-color);
  color: var(--black-color-3);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  z-index: 2;
}

.image-slot {
  position: relative;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
}

.image-slot img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.empty-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 2px dashed var(--black-color-1);
  border-radius: 8px;
  color: var(--white-color-2);
  transition: all 0.3s ease;
}

.empty-slot:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.empty-slot i {
  font-size: 2rem;
  margin-bottom: 8px;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .row.g-4 {
    flex-direction: column;
  }
  
  .image-slots-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
  
  .modal-dialog {
    margin: 10px;
  }
  
  .preview-img {
    height: 100px;
  }
}
</style>
